import { useState } from 'react';
import { testimonials } from '../../../mocks/testimonials';
import { useTranslation } from 'react-i18next';

const StarRating = ({ count }: { count: number }) => (
  <div className="flex items-center gap-0.5">
    {Array.from({ length: count }).map((_, i) => (
      <i key={i} className="ri-star-fill text-[#d4a017] text-sm" />
    ))}
  </div>
);

export const TestimonialsSection = () => {
  const { t } = useTranslation();
  const [activeIdx, setActiveIdx] = useState(0);
  const featured = testimonials[activeIdx];

  const previews = [1, 2, 3].map(
    offset => testimonials[(activeIdx + offset) % testimonials.length]
  );

  const stats = [
    { label: t('home_test_stat1'), value: '2.400+' },
    { label: t('home_test_stat2'), value: '4.9 / 5' },
    { label: t('home_test_stat3'), value: '87%' },
    { label: t('home_test_stat4'), value: '100+' },
  ];

  return (
    <section className="py-24 bg-gradient-to-b from-[#050010] to-[#0a0018] relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-violet-600/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-[1440px] mx-auto px-6 lg:px-12 relative z-10">
        {/* Header */}
        <div className="text-center mb-14">
          <p className="text-violet-400 text-xs font-semibold uppercase tracking-widest mb-3">{t('home_test_badge')}</p>
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            {t('home_test_title')}{' '}
            <span className="text-transparent bg-gradient-to-r from-violet-400 to-fuchsia-400 bg-clip-text">
              Blue Protect
            </span>
          </h2>
          <p className="text-white/35 text-sm max-w-lg mx-auto">
            {t('home_test_sub')}
          </p>
        </div>

        {/* Stats bar */}
        <div className="flex flex-wrap justify-center gap-8 mb-14">
          {stats.map(stat => (
            <div key={stat.label} className="text-center">
              <p className="text-2xl font-black text-white">{stat.value}</p>
              <p className="text-white/35 text-xs mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Main layout: featured + side cards */}
        <div className="flex flex-col lg:flex-row gap-5">
          {/* Featured review */}
          <div className="flex-1 relative bg-gradient-to-br from-violet-600/10 to-fuchsia-600/5 border border-violet-500/25 rounded-3xl p-8 lg:p-10 flex flex-col justify-between min-h-[340px]">
            <div className="absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-violet-500/50 to-transparent" />

            <div>
              <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
                <StarRating count={featured.rating} />
                {featured.product && (
                  <span className="px-3 py-1 bg-violet-500/10 border border-violet-500/20 text-violet-400 text-xs rounded-full whitespace-nowrap">
                    {featured.product}
                  </span>
                )}
              </div>

              <i className="ri-double-quotes-l text-5xl text-violet-500/20 leading-none mb-3 block" />
              <blockquote className="text-white/80 text-base lg:text-lg leading-relaxed italic font-light">
                {featured.message}
              </blockquote>
            </div>

            <div className="flex items-center gap-4 mt-8 pt-6 border-t border-white/5">
              <img
                src={featured.avatar}
                alt={featured.name}
                className="w-12 h-12 rounded-full object-cover object-top border-2 border-violet-500/30 shrink-0"
              />
              <div>
                <p className="text-white font-semibold text-sm">{featured.name}</p>
                <p className="text-white/35 text-xs">{featured.role}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 mt-6">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIdx(i)}
                  className={`h-1.5 rounded-full transition-all duration-300 cursor-pointer ${
                    i === activeIdx ? 'w-8 bg-violet-400' : 'w-2 bg-white/15 hover:bg-white/30'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Side preview cards */}
          <div className="flex flex-col gap-4 lg:w-80">
            {previews.map((testimonial, i) => {
              const realIdx = (activeIdx + i + 1) % testimonials.length;
              return (
                <button
                  key={testimonial.id}
                  onClick={() => setActiveIdx(realIdx)}
                  className="text-left bg-[#110020]/70 border border-violet-500/10 rounded-2xl p-5 cursor-pointer hover:border-violet-500/30 hover:bg-[#150028]/70 transition-all duration-200 group"
                >
                  <div className="flex items-center gap-3 mb-3">
                    <img
                      src={testimonial.avatar}
                      alt={testimonial.name}
                      className="w-9 h-9 rounded-full object-cover object-top border border-violet-500/20 shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-white font-semibold text-xs">{testimonial.name}</p>
                      <p className="text-white/30 text-xs truncate">{testimonial.role}</p>
                    </div>
                    <StarRating count={testimonial.rating} />
                  </div>
                  <p className="text-white/45 text-xs leading-relaxed line-clamp-2 group-hover:text-white/60 transition-colors">
                    {testimonial.message}
                  </p>
                  {testimonial.product && (
                    <span className="inline-block mt-2 px-2 py-0.5 bg-violet-500/8 border border-violet-500/15 text-violet-400/70 text-xs rounded-full">
                      {testimonial.product}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};
